import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useFilterByValue, useValueFacets } from '../common/FilterByValueContext';
import useStableCallback from '../../hooks/useStableCallback';
import useAccountNames from '../../hooks/useAccountNames';
import { columnFilterOf } from '../../utils/headerFilterModel';
import { itemTagEntries } from '../../utils/itemTags';
import { rememberedItemTagTitles } from '../../hooks/useTagSearch';
import { userIdsOf } from '../../utils/userElements';
import { normalizeId } from '../../utils/ids';
import { valueMenuItems, useValueActionRunner } from './CellValueMenu';
import CellMenuPopup from './CellMenuPopup';
import { elementValueMenu, hasValueActions, systemValueMenu } from './valueFilterOptions';

const OWN_COLUMNS = new Set(['sys:createdAt', 'sys:updatedAt', 'sys:owner', 'sys:tags']);

/**
 * The table's value menu (KPR-1640, KPE-051 ruling i07): ONE menu for the whole
 * table, opened by a cell (TableCell, through `CellMenuContext`) at a point.
 * Its rows are built when it opens, from the row as the table holds it, and a
 * filter is handed to the page's own door (`applyValueFilter` from
 * FilterByValueContext) which reads `?q=` at that moment, so a chip or the
 * builder changing the filter under an open menu is kept.
 *
 * It is on only where the items page gave the table a filter
 * (`headerMenu.filter`) and a door: a related list or the tag page keeps the
 * browser's menu.
 *
 * Returns `{ context, node }`: the value for `CellMenuContext.Provider` (stable
 * for the life of the table) and the menu to draw beside the table.
 */
export default function useCellValueMenu({
	headerMenu = null,
	defsByName,
	resolveLookupValue,
	unitSystem = null,
	tagVocabulary = null,
	privateTags = null,
}) {
	const applyValueFilter = useFilterByValue();
	const facetFor = useValueFacets();
	const fields = headerMenu?.filter?.fields;
	const enabled = Boolean(headerMenu?.filter) && typeof applyValueFilter === 'function';
	const [target, setTarget] = useState(null);
	const run = useValueActionRunner({ apply: applyValueFilter });

	// What the menu would say for a row and a column, now.
	const modelFor = useStableCallback(({ item, columnKey }, names = null) => {
		if (!enabled || !item || typeof columnKey !== 'string') return null;
		if (columnKey.startsWith('el:')) {
			const element = defsByName?.get(columnKey.slice(3));
			if (!element) return null;
			return elementValueMenu({
				element,
				value: item?.elements?.[element.name],
				column: columnFilterOf(columnKey, { defsByName, fields }),
				resolveLookupValue,
				facetFor,
				unitSystem,
				userNames: names,
			});
		}
		if (!OWN_COLUMNS.has(columnKey)) return null;
		return systemValueMenu({
			key: columnKey,
			item,
			column: columnFilterOf(columnKey, { defsByName, fields }),
			userNames: names,
			tagEntries: columnKey === 'sys:tags'
				? itemTagEntries(item, { vocabulary: tagVocabulary, privateTags, titles: rememberedItemTagTitles() })
				: [],
		});
	});

	const context = useMemo(() => ({
		canOpen: request => hasValueActions(modelFor(request)),
		open: ({ item, columnKey, cellEl, x, y }) => {
			if (!hasValueActions(modelFor({ item, columnKey }))) return false;
			setTarget({ item, columnKey, cellEl, x, y });
			return true;
		},
	}), [modelFor]);

	// A person is named by an account the table has not read yet: ask once, on open.
	const personIds = useMemo(() => {
		if (!target) return [];
		const { item, columnKey } = target;
		if (columnKey === 'sys:owner') return [normalizeId(item?.owner)].filter(Boolean);
		if (!columnKey.startsWith('el:')) return [];
		const element = defsByName?.get(columnKey.slice(3));
		return element?.dataType === 'user' ? userIdsOf(item?.elements?.[element.name]) : [];
	}, [target, defsByName]);
	const userNames = useAccountNames(personIds, Boolean(target));

	const model = useMemo(() => (target ? modelFor(target, userNames) : null), [target, userNames, modelFor]);
	const items = useMemo(() => valueMenuItems(model), [model]);

	const targetRef = useRef(null);
	targetRef.current = target;
	// Close, and give focus back to the cell unless the person moved it elsewhere.
	const close = useCallback(() => {
		const cell = targetRef.current?.cellEl;
		setTarget(null);
		setTimeout(() => {
			const holder = document.activeElement;
			if (cell && cell.isConnected && (!holder || holder === document.body || holder.closest?.('.cell-value-menu'))) cell.focus({ preventScroll: true });
		}, 0);
	}, []);

	const node = target && items.length ? (
		<CellMenuPopup
			x={target.x}
			y={target.y}
			items={items}
			onPick={(key) => {
				run(model, key, target.cellEl);
				close();
			}}
			onClose={close}
		/>
	) : null;

	return { context: enabled ? context : null, node };
}
